import { useMemo, useState, useEffect, useRef } from "react"
import { Link, useLocation } from "react-router-dom"

// styles
import "@/assets/scss/app/aside.scss"

// images
import brandImage from "@/assets/img/aside/brand.svg"
import accountingImage from "@/assets/img/aside/icons/accounting.svg"
import todoImage from "@/assets/img/aside/icons/todo.svg"
import dashboardImage from "@/assets/img/aside/icons/dashboard.svg"
import scheduleImage from "@/assets/img/aside/icons/schedule.svg"
import analyticsImage from "@/assets/img/aside/icons/analytics.svg"
import aiImage from "@/assets/img/aside/icons/ai.svg"
import partsImage from "@/assets/img/aside/icons/parts.svg"
import contractorsImage from "@/assets/img/aside/icons/contractors.svg"
import relationshipsImage from "@/assets/img/aside/icons/relationships.svg"
import communicationImage from "@/assets/img/aside/icons/communication.svg"
import companyImage from "@/assets/img/aside/icons/company.svg"
import websiteImage from "@/assets/img/aside/icons/website.svg"
import branchesImage from "@/assets/img/aside/icons/branches.svg"
import licensesImage from "@/assets/img/aside/icons/licenses.svg"
import smsImage from "@/assets/img/aside/icons/sms.svg"
import usersImage from "@/assets/img/aside/icons/users.svg"
import expandedImage from "@/assets/img/aside/icons/expanded.svg"
import minimizedImage from "@/assets/img/aside/icons/minimized.svg"

// providers
import { useAuthDataContext } from "@/_providers/Auth"
import { trans } from "@/_providers/Translation"
import { useNewReservationsContext } from "@/_providers/NewReservations"

const getAdminMenuItems = () => [
  {
    segment: "partners",
    route: "/partners",
    title: trans("nav.services"),
    permission: "partners",
    icon: contractorsImage,
    children: [],
  },
  {
    segment: "branches",
    route: "/branches",
    title: trans("nav.officeBranches"),
    permission: "branches",
    icon: branchesImage,
    children: [],
  },
  {
    segment: "licenses",
    route: "/licenses",
    title: trans("nav.licenses"),
    permission: "licenses",
    icon: licensesImage,
    children: [
      { route: "/licenses", title: trans("nav.mainLicenses") },
      {
        route: "/licenses/configurator",
        title: trans("nav.licensesConfigurator"),
      },
    ],
  },
  {
    segment: "sms-package",
    route: "/sms-package",
    title: trans("nav.smsPackets"),
    permission: "licenses",
    icon: smsImage,
    children: [],
  },
  {
    segment: "accounting",
    route: "/accounting",
    title: trans("nav.accounting"),
    permissions: ["invoices", "accounts", "expenses", "banktransactions"],
    icon: accountingImage,
    children: [
      {
        route: "/accounting",
        title: trans("nav.invoices"),
        permission: "invoices",
      },
      {
        route: "/accounting/accounts",
        title: trans("nav.accounts"),
        permission: "accounts",
      },
      {
        route: "/accounting/expenses",
        title: trans("nav.expenses"),
        permission: "expenses",
      },
      {
        route: "/accounting/banktransactions",
        title: trans("nav.banktransactions"),
        permission: "banktransactions",
      },
    ],
  },
  {
    segment: "todo",
    route: "/todo",
    title: trans("nav.todos"),
    permission: "todos",
    icon: todoImage,
    children: [],
  },
  {
    segment: "admins",
    route: "/admins",
    title: trans("nav.users"),
    superAdminOnly: true,
    icon: usersImage,
    children: [],
  },
  {
    segment: "settings",
    route: "/settings",
    title: trans("nav.settings"),
    superAdminOnly: true,
    icon: companyImage,
    children: [],
  },
]

const getServiceMenuItems = () => [
  {
    segment: "",
    route: "/",
    title: trans("nav.dashboard"),
    permission: "dashboard",
    icon: dashboardImage,
    children: [],
  },
  {
    segment: "schedule",
    route: "/schedule",
    title: trans("nav.schedule"),
    permission: "schedule",
    icon: scheduleImage,
    children: [
      {
        route: "/schedule",
        title: trans("nav.schedule"),
        permission: "schedule.plan",
      },
      {
        route: "/schedule/reservations",
        title: trans("nav.reservations"),
        permission: "schedule.reservations",
      },
      {
        route: "/schedule/manuals",
        title: trans("nav.handbooks"),
        permission: "schedule.manuals",
      },
    ],
  },
  {
    segment: "analytics",
    route: "/analytics",
    title: trans("nav.analyticsModule"),
    permission: "analytics",
    icon: analyticsImage,
    children: [
      {
        route: "/analytics",
        title: trans("nav.technicians"),
        permission: "analytics.technicians",
      },
      {
        route: "/analytics/realtime",
        title: trans("nav.realtime"),
        permission: "analytics.realtime",
      },
      {
        route: "/analytics/clients",
        title: trans("nav.analytics_clients"),
        permission: "analytics.clients",
      },
      {
        route: "/analytics/trends",
        title: trans("nav.analytics_trends"),
        permission: "analytics.trends",
      },
    ],
  },
  {
    segment: "parts",
    route: "/parts/storage",
    title: trans("nav.partsStorage"),
    permission: "parts",
    icon: partsImage,
    children: [
      { route: "/parts/storage", title: trans("nav.partsItems") },
      { route: "/parts/appliances", title: trans("nav.appliances") },
      //   { route: "/parts/list", title: "Склад" },
      { route: "/parts/most-used", title: trans("nav.most_used") },
      { route: "/parts/suppliers", title: trans("nav.suppliers") },
      { route: "/parts/stats", title: trans("nav.stats") },
      { route: "/parts/online-sales", title: trans("nav.onlineSales") },
    ],
  },
  {
    segment: "contractors",
    route: "/contractors?tab=dashboard",
    title: trans("nav.contractors"),
    permission: "contractors",
    icon: contractorsImage,
    children: [
      {
        route: "/contractors?tab=dashboard",
        title: trans("nav.companies"),
        permission: "contractors.companies",
      },
      {
        route: "/contractors/subscriptions",
        title: trans("nav.subscriptions"),
        permission: "contractors.companies",
      },
      {
        route: "/contractors/warranties",
        title: trans("nav.guarantees"),
        permission: "contractors.companies",
      },
      // {
      //   route: "/contractors?tab=dashboard",
      //   title: "Табло",
      //   permission: "contractors.companies",
      //   matchSearch: "tab=dashboard",
      // },
      // {
      //   route: "/contractors?tab=list",
      //   title: "Фирми",
      //   permission: "contractors.companies",
      //   matchSearch: "tab=list",
      // },
      // {
      //   route: "/contractors?tab=types&types=subscriptions",
      //   title: "Абонаменти",
      //   permission: "contractors.subscriptions",
      //   matchSearch: "tab=types",
      // },
      // {
      //   route: "/contractors?tab=stats",
      //   title: "Статистика",
      //   permission: "contractors.guarantees",
      //   matchSearch: "tab=stats",
      // },
      // {
      //   route: "/contractors?tab=servicing",
      //   title: "Сервизиране",
      //   permission: "contractors.guarantees",
      //   matchSearch: "tab=servicing",
      // },
    ],
  },
  {
    segment: "relationships",
    route: "/relationships?tab=dashboard",
    title: trans("nav.relationships"),
    permission: "partners",
    icon: relationshipsImage,
    children: [
      {
        route: "/relationships?tab=dashboard",
        title: trans("nav.dashboard"),
        permission: "partners.dashboard",
        matchSearch: "tab=dashboard",
      },
      {
        route: "/relationships?tab=list&page=1&group=sent",
        title: trans("nav.shared_from_us"),
        permission: "partners.shared_from_us",
        matchSearch: "group=sent",
      },
      {
        route: "/relationships?tab=list&page=1&group=received",
        title: trans("nav.shared_with_us"),
        permission: "partners.shared_with_us",
        matchSearch: "group=received",
      },
      {
        route: "/relationships/selfemployed",
        title: trans("nav.external_techs"),
        permission: "partners.external_techs",
      },
    ],
  },
  {
    segment: "accounting",
    route: "/accounting",
    title: trans("nav.accounting"),
    permissions: ["invoices", "accounts", "expenses", "banktransactions"],
    icon: accountingImage,
    children: [
      {
        route: "/accounting",
        title: trans("nav.invoices"),
        permission: "invoices",
      },
      {
        route: "/accounting/unpaid-reservations",
        title: trans("nav.unpaidReservations"),
        permission: "invoices",
      },
      {
        route: "/accounting/accounts",
        title: trans("nav.accounts"),
        permission: "accounts",
      },
      {
        route: "/accounting/expenses",
        title: trans("nav.expenses"),
        permission: "expenses",
      },
      {
        route: "/accounting/banktransactions",
        title: trans("nav.banktransactions"),
        permission: "banktransactions",
      },
    ],
  },
  {
    segment: "todo",
    route: "/todo",
    title: trans("nav.todos"),
    permission: "todos",
    icon: todoImage,
    children: [],
  },
  {
    segment: "communication",
    route: "/communication",
    title: trans("nav.communication"),
    permissions: ["sms", "voicecalls"],
    icon: communicationImage,
    children: [
      {
        route: "/communication",
        title: trans("nav.messages"),
        permission: "sms",
      },
      {
        route: "/communication/voicecalls",
        title: trans("nav.calls"),
        permission: "voicecalls",
      },
    ],
  },
  {
    segment: "company",
    route: "/company",
    title: trans("nav.settings"),
    permissions: ["company", "settings.main", "bonuspolicy"],
    icon: companyImage,
    children: [
      {
        route: "/company",
        title: trans("nav.main_settings"),
        permission: "settings.main",
      },
      {
        route: "/company/bonuspolicies",
        title: trans("nav.bonuspolicies"),
        permission: "bonuspolicy",
      },
    ],
  },
  {
    segment: "website",
    route: "/website",
    title: trans("nav.website"),
    permission: "website",
    icon: websiteImage,
    children: [],
  },
]

const getFirstSegment = (path) => {
  const cleanPath = path.split("?")[0] // Remove query string
  return cleanPath.split("/").filter(Boolean)[0] || ""
}

function Aside() {
  const newReservations = useNewReservationsContext()

  const { isAdmin, access, permission, isSuperAdmin, isManager } =
    useAuthDataContext()

  const location = useLocation()

  const MENU_ITEMS = useMemo(() => {
    const baseMenuItems = isAdmin()
      ? getAdminMenuItems()
      : getServiceMenuItems()

    return baseMenuItems
      .filter((item) => {
        if (item.superAdminOnly) {
          return isSuperAdmin()
        }

        if (item.permissions) {
          return item.permissions.some((p) => access(p) || permission(p))
        }

        if (item.permission) {
          if (item.permission === "dashboard") {
            return isManager() || permission(item.permission)
          }
          return access(item.permission) || permission(item.permission)
        }

        return true
      })
      .map((item) => {
        if (item.children && item.children.length > 0) {
          const filteredChildren = item.children.filter((child) => {
            if (!child.permission) return true
            return access(child.permission) || permission(child.permission)
          })
          return { ...item, children: filteredChildren }
        }
        return item
      })
  }, [isAdmin, isSuperAdmin, isManager, access, permission])

  const asideRef = useRef(null)

  const currentSegment = useMemo(
    () => getFirstSegment(location.pathname),
    [location.pathname],
  )

  const [expandedSegment, setExpandedSegment] = useState(currentSegment)

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("aside-collapsed") === "1",
  )

  // flyout shown on hover when the menu is collapsed
  // { segment, title, children, top, left }
  const [flyout, setFlyout] = useState(null)

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem("aside-collapsed", next ? "1" : "0")
      return next
    })
    setFlyout(null)
  }

  const handleParentEnter = (e, item) => {
    if (!collapsed || window.innerWidth <= 1100) return
    if (!item.children || item.children.length === 0) return
    const rect = e.currentTarget.getBoundingClientRect()
    setFlyout({
      segment: item.segment,
      title: item.title,
      children: item.children || [],
      top: rect.top,
      left: rect.right,
    })
  }

  const handleParentLeave = () => setFlyout(null)

  // let the header / content widen to fill the space freed by the collapsed
  // menu (they live outside <aside>, so we flag the state on <body>)
  useEffect(() => {
    document.body.classList.toggle("aside-collapsed", collapsed)
    return () => document.body.classList.remove("aside-collapsed")
  }, [collapsed])

  useEffect(() => {
    setExpandedSegment(currentSegment)
  }, [currentSegment])

  const handleParentClick = (e, segment, hasChildren) => {
    e.stopPropagation()
    if (hasChildren && window.innerWidth <= 1100) {
      e.preventDefault()
    } else if (!hasChildren) {
      asideRef.current.classList.remove("visable-aside")
      document.querySelector("body").classList.remove("blured-content")
    }
    setExpandedSegment((prev) => (prev === segment ? "" : segment))
  }

  const handleSubLinkClick = () => {
    asideRef.current.classList.remove("visable-aside")
    document.querySelector("body").classList.remove("blured-content")
    setExpandedSegment(currentSegment)
    setFlyout(null)
  }

  useEffect(() => {
    const el = asideRef.current
    if (!el) return

    const observer = new MutationObserver(() => {
      if (!el.classList.contains("visable-aside")) {
        setExpandedSegment(currentSegment)
      }
    })

    observer.observe(el, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [currentSegment])

  const isExpanded = (segment) => segment === expandedSegment

  const isActive = (item) => {
    if (item.matchSearch) {
      return location.search.includes(item.matchSearch)
    }

    const itemPath = item.route.split("?")[0]

    return location.pathname === itemPath
  }

  return (
    <aside ref={asideRef} className={collapsed ? "collapsed" : ""}>
      <div className="head">
        <div className="brand">
          <img src={brandImage} alt="Engineering United System" />
          {!collapsed && <p>Engineering United System</p>}
        </div>
        <button
          type="button"
          className="toggle-aside"
          onClick={toggleCollapsed}
          style={
            !collapsed
              ? {
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  minWidth: "40px",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "absolute",
                  top: "-20px",
                  right: "-5px",
                }
              : {}
          }
        >
          <img
            src={collapsed ? minimizedImage : expandedImage}
            alt=""
            className="expanded"
            style={{
              marginBottom: 0,
            }}
          />
        </button>
      </div>
      <ul className="main">
        {MENU_ITEMS.map((item) => {
          const { segment, route, title, children, icon } = item
          const hasChildren = children && children.length > 0
          const expanded = isExpanded(segment)
          const parentActive =
            expandedSegment && expandedSegment !== currentSegment
              ? segment === expandedSegment
              : segment === currentSegment

          return (
            <li
              key={segment}
              className={parentActive ? "active" : ""}
              onMouseEnter={(e) => handleParentEnter(e, item)}
              onMouseLeave={handleParentLeave}
            >
              <Link
                to={route}
                onClick={(e) => handleParentClick(e, segment, hasChildren)}
              >
                <img src={icon} alt="" />
                <span
                  style={{
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    color: "inherit",
                  }}
                >
                  {title}
                </span>
                {route === "/schedule" &&
                newReservations &&
                newReservations.count > 0 &&
                !(hasChildren && expanded) ? (
                  <span
                    className="badge"
                    style={{
                      backgroundColor: "#FFE695",
                      color: "#262F5F",
                    }}
                  >
                    {newReservations.count}
                  </span>
                ) : null}
                {route === "/analytics" ? (
                  <img
                    src={aiImage}
                    alt=""
                    style={{
                      marginTop: "-15px",
                      marginLeft: "-7px",
                    }}
                  />
                ) : null}
              </Link>
              {!collapsed && hasChildren && expanded && (
                <ul>
                  {children.map((child) => (
                    <li
                      key={child.route}
                      className={isActive(child) ? "active" : ""}
                    >
                      <Link to={child.route} onClick={handleSubLinkClick}>
                        {child.title}
                        {child.route === "/schedule/reservations" &&
                        newReservations &&
                        newReservations.count > 0 ? (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "#FFE695",
                              color: "#262F5F",
                            }}
                          >
                            {newReservations.count}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              {collapsed && flyout && flyout.segment === segment && (
                <div
                  className="aside-flyout"
                  style={{ top: flyout.top, left: flyout.left }}
                >
                  <p className="flyout-title">{title}</p>
                  {hasChildren && (
                    <ul>
                      {children.map((child) => (
                        <li
                          key={child.route}
                          className={isActive(child) ? "active" : ""}
                        >
                          <Link to={child.route} onClick={handleSubLinkClick}>
                            {child.title}
                            {child.route === "/schedule/reservations" &&
                            newReservations &&
                            newReservations.count > 0 ? (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: "#FFE695",
                                  color: "#262F5F",
                                }}
                              >
                                {newReservations.count}
                              </span>
                            ) : null}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </aside>
  )
}

export default Aside
